import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStockSnapshots } from "../marketData/marketDataApi";
import "./NinaDayScanner.css";

const REFRESH_MS = 60000;
const REFRESH_DEADLINE_MS = 30000;
const MAX_UNIVERSE = 50;
export const DEFAULT_UNIVERSE = [
  "PLUG", "AAPL", "NVDA", "AMD", "TSLA", "AMZN", "MSFT", "META",
  "GOOGL", "NFLX", "AVGO", "INTC", "SOFI", "HOOD", "COIN", "SMCI",
  "ARM", "PLTR", "RIVN", "LCID", "F", "NIO", "BAC", "JPM", "XOM",
  "CVX", "UBER", "SPY", "QQQ", "IWM", "SLV", "RXT",
];

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function firstFinite(...values) {
  for (const value of values) {
    const normalized = finiteNumber(value);
    if (normalized !== null) return normalized;
  }
  return null;
}

function formatPrice(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : `$${number.toFixed(2)}`;
}

function formatPercent(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return `${number >= 0 ? "+" : ""}${number.toFixed(2)}%`;
}

function formatVolume(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(number);
}

function formatTimestamp(value) {
  if (!value) return "Update unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Update unavailable";
  return parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function timestampMillis(value) {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function freshestPrice(latestTrade, minuteBar) {
  const tradePrice = finiteNumber(latestTrade?.p);
  const minutePrice = finiteNumber(minuteBar?.c);
  if (tradePrice === null) return minutePrice;
  if (minutePrice === null) return tradePrice;

  const tradeTime = timestampMillis(latestTrade?.t);
  const minuteTime = timestampMillis(minuteBar?.t);
  if (minuteTime !== null && (tradeTime === null || minuteTime > tradeTime)) {
    return minutePrice;
  }
  return tradePrice;
}

function normalizeSnapshot(symbol, snapshot) {
  const latestTrade = snapshot?.latest_trade || {};
  const latestQuote = snapshot?.latest_quote || {};
  const minuteBar = snapshot?.minute_bar || {};
  const dailyBar = snapshot?.daily_bar || {};
  const previousDailyBar = snapshot?.previous_daily_bar || {};
  const source = snapshot?.source || {};

  const lastPrice = freshestPrice(latestTrade, minuteBar);
  const previousClose = finiteNumber(previousDailyBar.c);
  const dollarChange =
    lastPrice !== null && previousClose !== null ? lastPrice - previousClose : null;
  const percentageChange =
    dollarChange !== null && previousClose > 0
      ? (dollarChange / previousClose) * 100
      : null;

  return {
    symbol,
    lastPrice,
    previousClose,
    dollarChange,
    percentageChange,
    bid: finiteNumber(latestQuote.bp),
    ask: finiteNumber(latestQuote.ap),
    dayHigh: finiteNumber(dailyBar.h),
    dayLow: finiteNumber(dailyBar.l),
    openPrice: finiteNumber(dailyBar.o),
    volume: finiteNumber(dailyBar.v),
    vwap: firstFinite(dailyBar.vw, minuteBar.vw),
    updatedAt:
      source.last_update_at || latestTrade.t || minuteBar.t || dailyBar.t || null,
    provider: source.provider || "Alpaca",
    feed: source.feed || "",
    quality: source.quality_level || source.quality_state || "Unknown",
    session: source.market_session || source.session || "Unknown",
    isFresh: source.is_fresh,
    notice: source.nina_data_quality_notice || "",
  };
}

function currentSelectedSymbol() {
  try {
    return String(localStorage.getItem("aninaSelectedSymbol") || "PLUG")
      .trim()
      .toUpperCase();
  } catch (error) {
    return "PLUG";
  }
}

function initialUniverse() {
  const selected = currentSelectedSymbol();
  return Array.from(new Set([selected, ...DEFAULT_UNIVERSE])).slice(0, MAX_UNIVERSE);
}

function normalizeAddedSymbols(value) {
  return String(value || "")
    .toUpperCase()
    .split(/[\s,]+/)
    .map((symbol) => symbol.trim())
    .filter((symbol) => /^[A-Z][A-Z0-9.-]{0,9}$/.test(symbol));
}

function NinaLiveDayScanner({
  onOpenTradeReview,
  refreshDeadlineMs = REFRESH_DEADLINE_MS,
  isActive = true,
}) {
  const [universe, setUniverse] = useState(() => initialUniverse());
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [refreshWarning, setRefreshWarning] = useState("");
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("change-desc");
  const [view, setView] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 640 ? "card" : "table"
  );
  const [symbolInput, setSymbolInput] = useState("");
  const refreshInFlightRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const loadSnapshots = useCallback(async ({ forceRefresh = false } = {}) => {
    if (refreshInFlightRef.current) return false;

    refreshInFlightRef.current = true;
    const isRefresh = hasLoadedOnceRef.current;
    const controller = new AbortController();
    let deadlineReached = false;
    const deadlineId = window.setTimeout(() => {
      deadlineReached = true;
      controller.abort();
    }, refreshDeadlineMs);

    setRefreshWarning("");
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setStatus("loading");
    }

    try {
      const payload = await getStockSnapshots(universe, undefined, {
        signal: controller.signal,
        ...(forceRefresh ? { cacheMode: "reload" } : {}),
      });
      if (deadlineReached || controller.signal.aborted) {
        setRefreshWarning(
          "Authenticated market-data refresh timed out. Previous verified rows were kept; try Refresh now again."
        );
        if (!isRefresh) setStatus("error");
        return false;
      }

      const snapshots = payload?.snapshots || {};
      const missingSymbols = new Set(payload?.missing_symbols || []);
      const liveRows = [];
      const nextErrors = [];

      universe.forEach((symbol) => {
        const snapshot = snapshots[symbol];
        if (snapshot) {
          liveRows.push(normalizeSnapshot(symbol, snapshot));
          return;
        }
        nextErrors.push({
          symbol,
          message: missingSymbols.has(symbol)
            ? "No authenticated Alpaca snapshot was returned."
            : "Authenticated Alpaca market data is unavailable.",
        });
      });

      setRows(liveRows);
      setErrors(nextErrors);
      setLastRefreshAt(new Date());
      setStatus(liveRows.length ? "ready" : "error");
      hasLoadedOnceRef.current = true;
      return true;
    } catch (error) {
      setRefreshWarning(
        controller.signal.aborted
          ? "Authenticated market-data refresh timed out. Previous verified rows were kept; try Refresh now again."
          : "Authenticated market-data refresh failed. Previous verified rows were kept; try Refresh now again."
      );
      if (!isRefresh) setStatus("error");
      return false;
    } finally {
      window.clearTimeout(deadlineId);
      refreshInFlightRef.current = false;
      setIsRefreshing(false);
    }
  }, [refreshDeadlineMs, universe]);

  useEffect(() => {
    if (!isActive) return undefined;
    let active = true;
    const run = async () => {
      if (!active) return;
      await loadSnapshots();
    };
    run();
    const timerId = window.setInterval(run, REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(timerId);
    };
  }, [isActive, loadSnapshots]);

  const visibleRows = useMemo(() => {
    const normalizedQuery = query.trim().toUpperCase();
    const filtered = normalizedQuery
      ? rows.filter((row) => row.symbol.includes(normalizedQuery))
      : [...rows];

    return filtered.sort((a, b) => {
      if (sort === "symbol-asc") return a.symbol.localeCompare(b.symbol);
      if (sort === "volume-desc") {
        return (finiteNumber(b.volume) ?? -Infinity) - (finiteNumber(a.volume) ?? -Infinity) ||
          a.symbol.localeCompare(b.symbol);
      }
      if (sort === "price-desc") {
        return (finiteNumber(b.lastPrice) ?? -Infinity) - (finiteNumber(a.lastPrice) ?? -Infinity) ||
          a.symbol.localeCompare(b.symbol);
      }
      return (finiteNumber(b.percentageChange) ?? -Infinity) -
        (finiteNumber(a.percentageChange) ?? -Infinity) || a.symbol.localeCompare(b.symbol);
    });
  }, [query, rows, sort]);

  const addSymbols = () => {
    const additions = normalizeAddedSymbols(symbolInput);
    if (!additions.length || isRefreshing || status === "loading") return;
    setUniverse((current) =>
      Array.from(new Set([...current, ...additions])).slice(0, MAX_UNIVERSE)
    );
    setSymbolInput("");
  };

  const openSymbol = (row) => {
    try {
      localStorage.setItem("aninaSelectedSymbol", row.symbol);
    } catch (error) {
      // The symbol-change event below still updates the active session.
    }

    window.dispatchEvent(
      new CustomEvent("nina:symbol-change", {
        detail: { symbol: row.symbol },
      })
    );

    if (typeof onOpenTradeReview === "function") {
      onOpenTradeReview({
        kind: "live-stock",
        symbol: row.symbol,
        source: "alpaca-live-day-scanner",
        provider: row.provider,
        feed: row.feed,
        dataQuality: row.quality,
        currentPrice: row.lastPrice,
      });
    }
  };

  return (
    <section className="ninaScannerPage" aria-labelledby="live-day-scanner-title">
      <header className="scannerHero scannerCompactPanel">
        <span className="scannerEyebrow">AUTHENTICATED MARKET DATA · LIVE</span>
        <h1 id="live-day-scanner-title">🔎 AI Day Scanner</h1>
        <p>
          Scan a broader, configurable stock universe using authenticated Alpaca snapshots.
          This view uses authenticated provider prices only and leaves values unavailable when provider data is missing.
        </p>
        <div className="scannerDemoNotice" role="note">
          <strong>Provider-safe live scanning</strong>
          <span>
            The scanner refreshes the bounded universe with one authenticated batch request once per minute.
            Prices, quotes, OHLC, volume, VWAP, feed, and freshness come only from authenticated snapshots.
          </span>
        </div>
      </header>

      <section className="scannerCompactPanel scannerLiveControls" aria-label="Live scanner controls">
        <div className="filterGrid">
          <label className="scannerFilter">
            <span>Search loaded symbols</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="PLUG" />
          </label>
          <label className="scannerFilter">
            <span>Sort</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="change-desc">% change</option>
              <option value="volume-desc">Volume</option>
              <option value="price-desc">Last price</option>
              <option value="symbol-asc">Symbol A–Z</option>
            </select>
          </label>
          <label className="scannerFilter">
            <span>Add symbols</span>
            <input
              value={symbolInput}
              onChange={(event) => setSymbolInput(event.target.value)}
              placeholder="RXT, SOFI, PLTR"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addSymbols();
                }
              }}
            />
          </label>
          <div className="scannerFilter scannerLiveControlActions">
            <span>Universe</span>
            <button
              type="button"
              onClick={addSymbols}
              disabled={
                !normalizeAddedSymbols(symbolInput).length || isRefreshing || status === "loading"
              }
            >
              Add symbols
            </button>
            <small>{universe.length}/{MAX_UNIVERSE} symbols · session-only customization</small>
          </div>
        </div>

        <div className="scannerStatusRow">
          <strong>
            {status === "loading"
              ? "Loading authenticated Alpaca snapshots…"
              : isRefreshing
              ? "Refreshing authenticated Alpaca snapshots…"
              : status === "error"
              ? "Live scanner data unavailable"
              : `${rows.length} authenticated symbols loaded`}
          </strong>
          <span>
            Showing {visibleRows.length} · {lastRefreshAt
              ? `Last refresh ${formatTimestamp(lastRefreshAt.toISOString())}`
              : "Waiting for first refresh"}
          </span>
          <button
            type="button"
            onClick={() => loadSnapshots({ forceRefresh: true })}
            disabled={status === "loading" || isRefreshing}
          >
            {isRefreshing ? "Refreshing…" : "Refresh now"}
          </button>
          <div className="viewToggle" role="group" aria-label="Scanner result view">
            <button type="button" aria-pressed={view === "table"} onClick={() => setView("table")}>Table</button>
            <button type="button" aria-pressed={view === "card"} onClick={() => setView("card")}>Cards</button>
          </div>
        </div>

        {refreshWarning && (
          <div className="scannerDemoNotice" role="alert">
            <strong>Refresh incomplete</strong>
            <span>{refreshWarning}</span>
          </div>
        )}

        {errors.length > 0 && (
          <div className="scannerDemoNotice" role="status">
            <strong>Partial provider coverage</strong>
            <span>{errors.map((error) => `${error.symbol}: ${error.message}`).join(" • ")}</span>
          </div>
        )}
      </section>

      <section className="scannerCompactPanel">
        {view === "table" ? (
          <div className="scannerTableContainer">
            <table className="scannerTable">
              <thead>
                <tr>
                  <th className="stickySymbol">Symbol</th>
                  <th>Last</th>
                  <th>Change</th>
                  <th>Bid</th>
                  <th>Ask</th>
                  <th>Day High</th>
                  <th>Day Low</th>
                  <th>Volume</th>
                  <th>VWAP</th>
                  <th>Provider / Feed</th>
                  <th>Quality</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.symbol} className="scannerTableRow">
                    <th scope="row" className="stickySymbol">
                      <button type="button" className="symbolSelect" onClick={() => openSymbol(row)}>
                        {row.symbol}
                      </button>
                      <span className="demoCellLabel">LIVE</span>
                    </th>
                    <td>{formatPrice(row.lastPrice)}</td>
                    <td className={finiteNumber(row.percentageChange) < 0 ? "market-loss" : "market-gain"}>
                      {formatPercent(row.percentageChange)}
                    </td>
                    <td>{formatPrice(row.bid)}</td>
                    <td>{formatPrice(row.ask)}</td>
                    <td>{formatPrice(row.dayHigh)}</td>
                    <td>{formatPrice(row.dayLow)}</td>
                    <td>{formatVolume(row.volume)}</td>
                    <td>{formatPrice(row.vwap)}</td>
                    <td>{row.provider} {row.feed ? `· ${row.feed.toUpperCase()}` : ""}</td>
                    <td>{row.quality}</td>
                    <td>{formatTimestamp(row.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="scannerLiveCardGrid">
            {visibleRows.map((row) => (
              <article className="scannerResult" key={row.symbol}>
                <div className="resultHeadline">
                  <div>
                    <span className="demoPill">LIVE · {row.feed ? row.feed.toUpperCase() : "ALPACA"}</span>
                    <h3>{row.symbol}</h3>
                    <p>{row.quality}</p>
                  </div>
                  <strong className={finiteNumber(row.percentageChange) < 0 ? "market-loss" : "market-gain"}>
                    {formatPercent(row.percentageChange)}
                  </strong>
                </div>
                <div className="resultMetrics">
                  <div><span>Last</span><strong>{formatPrice(row.lastPrice)}</strong></div>
                  <div><span>Volume</span><strong>{formatVolume(row.volume)}</strong></div>
                  <div><span>VWAP</span><strong>{formatPrice(row.vwap)}</strong></div>
                  <div><span>Day range</span><strong>{formatPrice(row.dayLow)} – {formatPrice(row.dayHigh)}</strong></div>
                </div>
                <div className="resultActions">
                  <button type="button" onClick={() => openSymbol(row)}>Open {row.symbol} in Live Trading</button>
                  <span>Updated {formatTimestamp(row.updatedAt)}</span>
                </div>
              </article>
            ))}
          </div>
        )}

        {status !== "loading" && !isRefreshing && visibleRows.length === 0 && (
          <div className="scannerDemoNotice" role="alert">
            <strong>No authenticated scanner rows match.</strong>
            <span>Adjust the search or refresh. No fallback market values have been fabricated or substituted.</span>
          </div>
        )}
      </section>

      <section className="scannerCompactPanel scannerCompactText">
        <h2>Current evidence boundary</h2>
        <p>
          MACD, relative volume, Level II, Time &amp; Sales, options flow, news, sector strength,
          institutional flow, and AI readiness scoring remain unavailable here until their
          genuine inputs and calculations are connected. The scanner does not invent them.
        </p>
      </section>
    </section>
  );
}

export default NinaLiveDayScanner;
